<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <!-- Header with Status and Actions -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>

              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h1 class="text-2xl font-bold text-gray-900">
                    {{ $t('ClaimCertifiedCosts.title') }}
                  </h1>

                  <template
                    v-if="claim"
                  >
                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      {{ $t('ClaimCertifiedCosts.claimPrefix') }} {{ claim.claim_number }}
                    </h2>

                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusBadgeClass(claim.status)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getStatusBadgeDotClass(claim.status)"
                      />
                      {{ ClaimStatusTitles[enumKeyFromValue(ClaimStatus, claim.status)] }}
                    </span>

                    <span
                      v-if="claim.status === ClaimStatus.certified"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {{ claim.stat_dec_received ? $t('ClaimCertifiedCosts.statusDec.received') : $t('ClaimCertifiedCosts.statusDec.notReceived') }}
                    </span>
                  </template>
                </div>

                <div class="flex items-center space-x-3">
                  <p
                    v-if="claim"
                    class="text-sm text-gray-600 mt-1"
                  >
                    {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center space-x-3">
              <!-- Send Draft Claim Button -->
              <button
                v-if="claim?.status === ClaimStatus.draft_pqs"
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="sendContractor"
              >
                <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                <span>{{ $t('ClaimCertifiedCosts.button.sendDraftClaim') }}</span>
              </button>

              <!-- Submit Claim Button -->
              <button
                v-if="claim?.status === ClaimStatus.contractor_claim && canEditClaim"
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="sendPqs"
              >
                <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                <span>{{ $t('ClaimCertifiedCosts.button.submitClaim') }}</span>
              </button>

              <!-- Issue Certificate Button -->
              <button
                v-if="claim?.status === ClaimStatus.pqs_review && canEditCertified"
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                :class="{ 'opacity-50': submitAction.is(States.LOADING) }"
                :disabled="submitAction.is(States.LOADING)"
                @click="submit"
              >
                <AppSpinner
                  v-if="submitAction.is(States.LOADING)"
                  class="w-4 h-4"
                />

                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else
                 
                  class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                <span>{{ $t('ClaimCertifiedCosts.button.certifyClaim') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <ClaimTable
          :claim-id="claimId"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ClaimTable from '@/components/ClaimCertifiedCosts/ClaimTable.vue';
import { useSignedData } from '@/composables/useSignedData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, enumKeyFromValue, parseUrlParams, timeZone } from '@/helpers';
import { t } from '@/i18n';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import TradeItem from '@/models/TradeItem';
import { VariationOrderType } from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { useClaimsStore } from '@/store/claims';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useTradeItemCostsStore } from '@/store/tradeItemCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrderCostsStore } from '@/store/variationOrderCosts';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { Collection } from 'pinia-orm';
import Swal from 'sweetalert2';
import { computed, onMounted } from 'vue';

const props = defineProps<{
  claimId: string;
}>();

const signedParams = useSignedData();
const tradeItemCostsStore = useTradeItemCostsStore();
const tradeItemsStore = useTradeItemsStore();
const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCostsStore = useVariationOrderCostsStore();
const variationOrderCategoriesStore = useVariationOrderCategoriesStore();
const sharedFundsStore = useSharedFundsStore();

tradeItemCostsStore.models.save(signedParams.models.tradeItemCosts);
tradeItemsStore.models.save(signedParams.models.tradeItems);
claimsStore.models.save(signedParams.models.claim);
tradesStore.models.save(signedParams.models.trades);
variationOrdersStore.models.save(signedParams.models.variationOrders);

if(signedParams.models.variation_order_categories) {
  variationOrderCategoriesStore.models.save(signedParams.models.variation_order_categories);
}

if(signedParams.models.sharedFunds) {
  sharedFundsStore.models.save(signedParams.models.sharedFunds);
}

const claim = computed(() => {
  return claimsStore.models
    .with('trades', (query) => {
      query.with('trade_items', (query) => {
        query.where('has_children', false);
      });
    })
    .with('trade_item_costs')
    .with('variation_orders', (query) => {
      query.whereHas('costs', (query) => {
        query.where('claim_id', parseInt(props.claimId));
      });
    })
    .with('variation_order_costs')
    .find(parseInt(props.claimId));
});

const tradeItemIds = computed(() => {
  return (signedParams.models.tradeItems as TradeItem[]).map((tradeItem) => {
    return tradeItem.id;
  });
});

const trades = computed(() => {
  return tradesStore.models.with('trade_items').whereHas('trade_items', (query) => {
    query.whereIn('id', tradeItemIds.value);
  }).with('trade_item_costs', (query) => {
    query.where('claim_id', parseInt(props.claimId));
  }).get();
});

const tradeItems = computed(() => {
  return tradeItemsStore.models.query().whereIn('id', tradeItemIds.value).with('trade_item_costs', (query) => {
    query.where('claim_id', parseInt(props.claimId));
  }).get();
});

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (claim.value.status === ClaimStatus.contractor_claim || claim.value.status === ClaimStatus.draft_contractor);
});

const canEditCertified = computed(() => {
  return signedParams.meta.edit_certified && claim.value.status === ClaimStatus.pqs_review;
});

onMounted(() => {
  const costs = tradeItems.value.map((trade_item) => {
    const cost = trade_item.trade_item_costs[0];

    if(canEditClaim.value) {
      return {
        current_claim: cost ? cost.current_claim : 0,
        id: cost?.id,
        claim_id: parseInt(props.claimId),
        trade_item_id: trade_item.id,
      };
    }

    return {
      current_certified: cost ? cost.current_certified : 0,
      id: cost?.id,
      claim_id: parseInt(props.claimId),
      trade_item_id: trade_item.id,
    };
  });

  tradeItemCostsStore.costsForm.setData({
    costs,
  });
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
    },
  };

  if(claim.value) {
    trades.value.forEach((trade) => {
      trade.trade_items.forEach((tradeItem) => {
        totals.trades.budget += tradeItem.budget;
        totals.trades.previous += tradeItem.previous(claim.value);
      });
    });

    claim.value.trade_item_costs.forEach((cost) => {
      totals.trades.current_claim += cost.current_claim;
      totals.trades.current_certified += cost.current_certified;
    });

    claim.value.variation_orders?.forEach((variationOrder) => {
      totals.variationOrders.budget += variationOrder.budget;
    });

    claim.value.variation_order_costs?.forEach((variationOrderCost) => {
      totals.variationOrders.current_claim += variationOrderCost.current_claim;
      totals.variationOrders.current_certified += variationOrderCost.current_certified;
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  return totals;
});

const sendContractorAction = useStoreApiAction(claimsStore.sendContractor);

const sendContractorParams = computed(() => {
  return parseUrlParams(signedParams.meta.send_contractor_url as string);
});

const sendContractor = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.alert.sendDraftTitle'),
    text: t('ClaimCertifiedCosts.alert.sendDraftText'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      sendContractorAction.request(claim.value.id, sendContractorParams.value, {
        trade_item_ids: Array.from(tradeItemsStore.selectedTradeItems.values()),
        draft_issued_at_time_zone: timeZone,
      }).then(() => {
        Swal.fire({
          icon: 'success',
          title: t('ClaimCertifiedCosts.alert.claimSentSuccess'),
        });
      });
    }
  });
};

const sendPqsAction = useStoreApiAction(claimsStore.sendPqs);

const sendPqsParams = computed(() => {
  return parseUrlParams(signedParams.meta.send_pqs_url as string);
});

const sendPqs = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.alert.sendPqsTitle'),
    text: t('ClaimCertifiedCosts.alert.sendPqsText'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      sendPqsAction.request(claim.value.id, sendPqsParams.value, {
        claim_issued_at_time_zone: timeZone,
      }).then(() => {
        Swal.fire({
          icon: 'success',
          title: t('ClaimCertifiedCosts.alert.claimSentPqsSuccess'),
        });
      }).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('ClaimCertifiedCosts.alert.sendPqsError'),
          text: error?.data?.message,
        });
      });
    }
  });
};

const submitAction = useStoreApiAction(claimsStore.submit);

const submitParams = computed(() => {
  return parseUrlParams(signedParams.meta.certify_url as string);
});

const submit = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.alert.certifyTitle'),
    text: t('ClaimCertifiedCosts.alert.certifyText', { date: new Date().toLocaleString() }),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      Swal.fire({
        icon: 'warning',
        text: t('ClaimCertifiedCosts.alert.statDecQuestion'),
        showConfirmButton: true,
        confirmButtonText: t('ClaimCertifiedCosts.alert.yes'),
        showDenyButton: true,
      }).then((result) => {
        if(result.isConfirmed || result.isDenied) {
          submitAction.request(claim.value.id, submitParams.value, {
            stat_dec_received: result.value,
            submitted_at_time_zone: timeZone,
          }).then(() => {
            Swal.fire({
              icon: 'success',
              title: t('ClaimCertifiedCosts.alert.claimCertifiedSuccess'),
            });
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              title: t('ClaimCertifiedCosts.alert.certifyError'),
              text: t('ClaimCertifiedCosts.alert.certifyErrorMessage'),
            });
          });
        }
      });
    }
  });
};

const variationOrderCosts = computed(() => {
  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrderCost> }> = {};

  for(const key in VariationOrderType) {
    costs[key] = variationOrderCostsStore.models.with('variation_order').where('claim_id', claim.value?.id).whereHas(
      'variation_order',
      (query) => {
        query.where('type', VariationOrderType[key]);
      },
    ).get();
  }

  return costs;
});

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    case ClaimStatus.pqs_review:
      return 'bg-yellow-100 text-yellow-800';

    case ClaimStatus.contractor_claim:
      return 'bg-blue-100 text-blue-800';

    case ClaimStatus.draft_pqs:
      return 'bg-gray-100 text-gray-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    case ClaimStatus.pqs_review:
      return 'bg-yellow-400';

    case ClaimStatus.contractor_claim:
      return 'bg-blue-400';

    case ClaimStatus.draft_pqs:
      return 'bg-gray-400';

    default:
      return 'bg-red-400';
  }
};
</script>

<style scoped></style>
